/**
 * Capture + persist a partner attribution slug from the URL.
 *
 * Why this exists
 * ───────────────
 * Without this, the only way to attribute a freshly-created merchant
 * account to a partner (BNP, FMJ, MerchantSecure, ...) is:
 *   - PARTNER MODE — partner has its own DNS + Clerk satellite. Works
 *     for testpartner / suby; doesn't apply to most resellers.
 *   - Backfill scripts (e.g. `scripts/sync-bnp-partner-chain.ts`)
 *     run AFTER the merchant has signed up, after they may have
 *     already generated transactions at the wrong rate / wrong
 *     attribution. Strictly post-hoc.
 *
 * This utility lets a partner share a sign-up URL like:
 *   https://app.tagada.io/sign-up?partner=bnp
 *
 * The slug is captured on landing, persisted across the OAuth bounce
 * + OTP step, and surfaced to the backend at account-provisioning
 * time. The backend then sets `accounts.partner` + the matching
 * `org.publicMetadata.whitelabel` via the same `setOrgWhitelabel`
 * service used by the superadmin retag flow — so the new account is
 * fully attributed from the very first transaction.
 *
 * Persistence strategy
 * ────────────────────
 *  - Cookie (Path=/, SameSite=Lax, 90 days) — survives OAuth bounces
 *    to/from Clerk, top-level redirects, and reads on the same
 *    origin from any subpath. SameSite=Lax (not Strict) so we keep
 *    the value when the user follows a link from an external email
 *    or Slack into our sign-up page.
 *  - localStorage (90 days simulated via `expires` field) — backup
 *    when cookies are dropped (Safari ITP edge cases) and for
 *    cross-tab consistency in the wizard.
 *
 * Reads prefer the cookie (more reliable across redirects). On every
 * landing we re-capture from URL if present and overwrite both
 * stores so the freshest signal wins. After the backend has stamped
 * the attribution we call `clearCapturedPartner()` to avoid carrying
 * a stale value into a second account creation.
 *
 * Validation
 * ──────────
 * Client-side: SHAPE only (lowercase a-z0-9_- regex, 1..32 chars).
 * Server-side is the source of truth — the backend rejects unknown
 * slugs via `isKnownPartnerAsync()` against the `partners` table
 * (see `src/lib/partners/deals.ts`). We deliberately don't ship the
 * slug whitelist to the client to avoid coupling deploy artefacts
 * to the partner roster.
 */

const COOKIE_NAME = '_tgd_partner_attr';
const STORAGE_KEY = 'crm_partner_attribution';
const TTL_DAYS = 90;
const TTL_SECONDS = TTL_DAYS * 24 * 60 * 60;
const TTL_MS = TTL_SECONDS * 1000;

/**
 * Same shape constraint as the server-side `partnerSlugSchema` in
 * `src/app/services/signupAttribution.ts`. Keep these in sync.
 */
const PARTNER_SLUG_RE = /^[a-z0-9][a-z0-9_-]{0,31}$/;

/**
 * Reserved slugs that must NEVER be captured as a partner attribution.
 * `tagadapay` is the default brand; capturing it would mean "no
 * partner" and pollute every analytics query.
 */
const RESERVED_SLUGS = new Set(['tagadapay']);

interface StoredAttribution {
  slug: string;
  capturedAt: number;
  /** Epoch ms, after which the value is considered expired. */
  expires: number;
  /** Where the slug came from. Useful for debugging & ops triage. */
  source: 'url';
}

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined';

const normaliseSlug = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const slug = String(raw).trim().toLowerCase();
  if (!slug) return null;
  if (RESERVED_SLUGS.has(slug)) return null;
  if (!PARTNER_SLUG_RE.test(slug)) return null;
  return slug;
};

// ─── cookie helpers ───────────────────────────────────────────────

const setCookie = (name: string, value: string, maxAgeSec: number): void => {
  if (!isBrowser()) return;
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `Path=/`,
    `Max-Age=${maxAgeSec}`,
    `SameSite=Lax`,
  ];
  // Production: served over HTTPS. Local dev (http) can also accept
  // the cookie because we omit Secure when not on HTTPS.
  if (window.location.protocol === 'https:') parts.push('Secure');
  document.cookie = parts.join('; ');
};

const getCookie = (name: string): string | null => {
  if (!isBrowser()) return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const deleteCookie = (name: string): void => {
  if (!isBrowser()) return;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
};

// ─── public API ───────────────────────────────────────────────────

/**
 * Read `?partner=<slug>` from a URLSearchParams instance (or the
 * current window's search). Returns the normalised slug or null.
 *
 * Caller is expected to invoke `persistCapturedPartner(result)` if a
 * non-null slug is returned. We split read & persist so React
 * components can decide their own effect-dependency lifecycle.
 */
export function readPartnerFromUrl(searchParams?: URLSearchParams): string | null {
  if (!isBrowser()) return null;
  const params = searchParams ?? new URLSearchParams(window.location.search);
  return normaliseSlug(params.get('partner'));
}

/**
 * Persist a freshly-captured slug to BOTH cookie and localStorage.
 * Overwrites any existing attribution — first-attribution-wins
 * semantics are enforced server-side, not here. This way a user
 * who visits twice with two different `?partner=` values still
 * gets the latest signal sent to the backend, and the backend
 * decides whether to honour it (only if the account doesn't yet
 * exist with a different partner).
 */
export function persistCapturedPartner(slug: string): void {
  const normalised = normaliseSlug(slug);
  if (!normalised) return;
  setCookie(COOKIE_NAME, normalised, TTL_SECONDS);
  if (!isBrowser()) return;
  try {
    const payload: StoredAttribution = {
      slug: normalised,
      capturedAt: Date.now(),
      expires: Date.now() + TTL_MS,
      source: 'url',
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage may be disabled (private browsing, quota). The
    // cookie is still set — that's the primary store. Swallow.
  }
}

/**
 * One-shot: read the URL, normalise, persist if non-null. Returns
 * the captured slug (or null if none / invalid). Use this in a
 * `useEffect` on every sign-up page so the value is captured even
 * if the user lands directly on a step that's not the first one.
 */
export function captureAndPersistPartnerFromUrl(searchParams?: URLSearchParams): string | null {
  const slug = readPartnerFromUrl(searchParams);
  if (slug) persistCapturedPartner(slug);
  return slug;
}

/**
 * Read the persisted attribution. Cookie wins over localStorage
 * because the cookie crosses Clerk's OAuth bounces more reliably
 * (different storage scope per origin during OAuth flow on some
 * browsers).
 */
export function getCapturedPartner(): string | null {
  if (!isBrowser()) return null;

  const cookieVal = normaliseSlug(getCookie(COOKIE_NAME));
  if (cookieVal) return cookieVal;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAttribution;
    if (typeof parsed.expires === 'number' && parsed.expires < Date.now()) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return normaliseSlug(parsed.slug);
  } catch {
    return null;
  }
}

/**
 * Clear both stores. Call this after the backend has confirmed
 * the attribution was applied (or rejected as conflict) so a
 * subsequent sign-up in the same browser doesn't carry over a
 * stale value to a different person's account.
 */
export function clearCapturedPartner(): void {
  deleteCookie(COOKIE_NAME);
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* swallow */
  }
}

/**
 * Resolve the partner slug to send to the backend at provisioning
 * time. Priority:
 *   1. `hostBased` (Partner Mode — `currentPartnerBrand()`). Stronger
 *      because the host is in our registry; it can't be spoofed.
 *   2. Captured-from-URL (this module). Soft signal; server still
 *      validates against the registry.
 *
 * Returns null if neither is set.
 */
export function resolveSignupPartner(hostBased: string | null): string | null {
  if (hostBased) return hostBased;
  return getCapturedPartner();
}
